import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Tab,
  Tabs,
  Autocomplete,
  ListItem,
  ListItemText,
  Stack
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { Intervention, Student, Task } from '@/types';
import { mockStudents } from '@/data/mockStudents';
import { TaskList } from './TaskManagement/TaskList';
import { TaskForm } from './TaskManagement/TaskForm';
import { TaskDetails } from './TaskManagement/TaskDetails';
import { generateInterventionTasks } from '../utils/taskUtils';

interface InterventionFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (intervention: Omit<Intervention, 'id'> & { student?: Student; id?: string }) => void;
  initialData?: (Intervention & { student?: Student }) | null;
}

const INTERVENTION_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Group' },
  { value: 'family', label: 'Family' }
];

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_process', label: 'In Process' },
  { value: 'completed', label: 'Completed' },
  { value: 'frozen', label: 'Frozen' }
];

export const InterventionForm: React.FC<InterventionFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData = null
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    type: 'individual' as Intervention['type'],
    date: new Date().toISOString().split('T')[0],
    notes: '',
    outcome: '',
    status: 'not_started' as Intervention['status'],
    tasks: [] as Task[]
  });
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        type: initialData.type,
        date: initialData.date,
        notes: initialData.notes,
        outcome: initialData.outcome,
        status: initialData.status || 'not_started',
        tasks: initialData.tasks || []
      });
      setSelectedStudent(initialData.student || null);
    } else {
      setFormData({
        type: 'individual',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        outcome: '',
        status: 'not_started',
        tasks: []
      });
      setSelectedStudent(null);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;

    const interventionId = initialData?.id || `int-${Date.now()}`;
    const tasks = formData.tasks.length > 0 ? 
      formData.tasks : 
      generateInterventionTasks(selectedStudent.id, interventionId);

    onSubmit({
      ...formData,
      student: selectedStudent,
      id: interventionId,
      tasks
    });
  };

  const handleAddTask = () => {
    setSelectedTask(null);
    setIsTaskFormOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setIsTaskFormOpen(true);
  };

  const handleSaveTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (selectedTask) {
      setFormData(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => 
          t.id === selectedTask.id ? { ...selectedTask, ...taskData } : t
        )
      }));
    } else {
      const newTask: Task = {
        ...taskData,
        id: `task-${Date.now()}`,
        assignedTo: selectedStudent?.id || '',
        reminders: [],
        attachments: [],
        comments: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setFormData(prev => ({
        ...prev,
        tasks: [...prev.tasks, newTask]
      }));
    }
    setIsTaskFormOpen(false);
    setSelectedTask(null);
  };

  const handleDeleteTask = (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      setFormData(prev => ({
        ...prev,
        tasks: prev.tasks.filter(t => t.id !== taskId)
      }));
      if (viewingTask?.id === taskId) {
        setViewingTask(null);
      }
    }
  };

  const handleUpdateTask = (taskId: string, updates: Partial<Task>) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => 
        t.id === taskId ? { ...t, ...updates } : t
      )
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {initialData ? 'Edit Intervention' : 'New Intervention'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Details" value="details" />
                <Tab label={`Tasks (${formData.tasks.length})`} value="tasks" />
              </Tabs>
            </Box>

            <TabPanel value="details" sx={{ p: 0 }}>
              <Stack spacing={3}>
                <Autocomplete
                  options={mockStudents}
                  value={selectedStudent}
                  onChange={(_, newValue) => setSelectedStudent(newValue)}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (Grade ${option.grade})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Student"
                      required
                      placeholder="Start typing student name..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <ListItem {...props}>
                      <ListItemText
                        primary={`${option.firstName} ${option.lastName}`}
                        secondary={`Grade ${option.grade} • ID: ${option.id}`}
                      />
                    </ListItem>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />

                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Intervention['type'] })}
                    required
                  >
                    {INTERVENTION_TYPES.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  required
                  fullWidth
                />

                <TextField
                  label="Outcome"
                  multiline
                  rows={2}
                  value={formData.outcome}
                  onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                  required
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Intervention['status'] })}
                    required
                  >
                    {STATUS_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </TabPanel>

            <TabPanel value="tasks" sx={{ p: 0 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleAddTask}
                    startIcon={<AddIcon />}
                  >
                    Add Task
                  </Button>
                </Box>

                <TaskList
                  tasks={formData.tasks}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  onView={setViewingTask}
                />
              </Stack>
            </TabPanel>
          </TabContext>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={!selectedStudent}
          >
            {initialData ? 'Save Changes' : 'Create Intervention'}
          </Button>
        </DialogActions>
      </form>

      <TaskForm
        open={isTaskFormOpen}
        task={selectedTask}
        onClose={() => setIsTaskFormOpen(false)}
        onSave={handleSaveTask}
      />

      <TaskDetails
        open={!!viewingTask}
        task={viewingTask}
        onClose={() => setViewingTask(null)}
        onUpdate={handleUpdateTask}
      />
    </Dialog>
  );
};